import * as React from "react"
import { X } from "lucide-react"

import { useVoz } from "@/lib/voz"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

/**
 * LA NOTA DE MARTY — el envase de todo mensaje en primera persona.
 *
 * Brandbook, lamina 20: «Marty habla en primera persona, corto, sobre fondo
 * rayado con filete rojo». La clase `.marty-nota` de `index.css` es ese envase;
 * esto es el comportamiento.
 *
 * SE PUEDE SILENCIAR, y esa es la decision de diseno que importa. La nota
 * explica para que sirve la pantalla, asi que le sirve a quien llega y estorba a
 * quien pasa ahi el dia. Una explicacion que no se puede cerrar es un cartel; el
 * cierre se recuerda por seccion en `localStorage`, de modo que silenciar el
 * diario no silencia la conciliacion.
 *
 * `localStorage` puede no estar (modo privado, cookies bloqueadas) y puede
 * lanzar al leerse: se envuelve en try/catch y, si falla, la nota simplemente
 * se muestra. Perder la preferencia es un detalle; no pintar la pantalla, no.
 */

const PREFIJO = "marty.nota."

function leerSilenciada(clave: string): boolean {
  try {
    return window.localStorage.getItem(PREFIJO + clave) === "1"
  } catch {
    return false
  }
}

function guardarSilenciada(clave: string): void {
  try {
    window.localStorage.setItem(PREFIJO + clave, "1")
  } catch {
    /* Sin almacenamiento la nota vuelve a salir la proxima vez. Es aceptable. */
  }
}

interface MartyNotaProps {
  /**
   * El texto. Si no se pasa, se usa la APERTURA de la seccion en la que se
   * monta el componente — que es el caso normal.
   */
  children?: React.ReactNode
  /**
   * Clave con la que se recuerda el silencio. Por defecto, el ambito de la
   * ruta: asi cada seccion se silencia por separado.
   */
  clave?: string
  /** `false` para una nota que no se puede cerrar (un aviso, no una guia). */
  silenciable?: boolean
  className?: string
}

export function MartyNota({ children, clave, silenciable = true, className }: MartyNotaProps) {
  const { ambito, apertura } = useVoz()
  const claveReal = clave ?? ambito
  // Se lee en el inicializador y no en un efecto: si se leyera despues, la nota
  // aparecería y desaparecería en el primer pintado.
  const [silenciada, setSilenciada] = React.useState(() =>
    silenciable ? leerSilenciada(claveReal) : false,
  )

  // Al cambiar de seccion cambia la clave, y con ella la preferencia.
  React.useEffect(() => {
    setSilenciada(silenciable ? leerSilenciada(claveReal) : false)
  }, [claveReal, silenciable])

  if (silenciada) return null

  const texto = children ?? apertura
  if (!texto) return null

  return (
    <div
      className={cn("marty-nota flex items-start gap-3 text-xs leading-[1.2rem]", className)}
      /* `status` y no `alert`: informa, no interrumpe. El brandbook es explicito
         en que las alertas de Marty no gritan. */
      role="status"
    >
      <p className="min-w-0 flex-1 text-foreground/90">{texto}</p>
      {silenciable ? (
        <button
          type="button"
          onClick={() => {
            guardarSilenciada(claveReal)
            setSilenciada(true)
          }}
          className={cn(
            "-mr-1 -mt-0.5 shrink-0 rounded p-1 text-muted-foreground",
            "transition-colors hover:bg-accent hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
          aria-label={t("No volver a mostrar esta nota en esta seccion")}
          title={t("No volver a mostrarla aqui")}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  )
}
